import axios from "axios";

const client = axios.create({
  baseURL: "/api",
  timeout: 45000,
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event("silapdi:unauthorized"));
    }
    return Promise.reject(error);
  },
);

const inFlightRequests = new Map();

/* =========================================================
   NORMALIZER
========================================================= */

const normalizeRW = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value).replace(/[^\d]/g, "");
  return s ? s.padStart(2, "0") : "";
};

const normalizeRT = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value).replace(/[^\d]/g, "");
  return s ? s.padStart(3, "0") : "";
};

const normalizeMonth = (value) =>
  String(value || "").trim().toLowerCase();

/* =========================================================
   GENERIC APPS SCRIPT REQUEST
========================================================= */

const request = (action, params = {}) => {
  const key = JSON.stringify([action, params]);
  const existingRequest = inFlightRequests.get(key);

  if (existingRequest) return existingRequest;

  const pendingRequest = client
    .get(`/${action}`, { params })
    .then((response) => {
      const data = response?.data;

      if (!data || typeof data !== "object") {
        throw new Error("Response API tidak valid.");
      }

      if (data.success === false) {
        throw new Error(
          data.message ||
          data.error ||
          "Google Apps Script API mengembalikan error."
        );
      }

      return data;
    });

  inFlightRequests.set(key, pendingRequest);

  return pendingRequest.finally(() => {
    if (inFlightRequests.get(key) === pendingRequest) {
      inFlightRequests.delete(key);
    }
  });
};

/* =========================================================
   MASTER WILAYAH
========================================================= */

const REGION_MASTER = [
  { rw: "01", jumlah_rt: 6 },
  { rw: "02", jumlah_rt: 6 },
  { rw: "03", jumlah_rt: 6 },
  { rw: "04", jumlah_rt: 9 },
  { rw: "05", jumlah_rt: 10 },
  { rw: "06", jumlah_rt: 4 },
  { rw: "07", jumlah_rt: 9 },
  { rw: "08", jumlah_rt: 8 },
  { rw: "09", jumlah_rt: 8 },
  { rw: "10", jumlah_rt: 9 },
];

export const regions = async () => ({
  kelurahan: "Suka Asih",
  kota: "Bandung",
  rw_list: REGION_MASTER,
  total_rw: 10,
  total_rt: 75,
});

export const listRt = async (rw) => {
  const rwClean = normalizeRW(rw);
  const found = REGION_MASTER.find((x) => x.rw === rwClean);

  if (!found) {
    return {
      success: false,
      rw: rwClean,
      total_rt: 0,
      data: [],
    };
  }

  return {
    success: true,
    rw: rwClean,
    total_rt: found.jumlah_rt,
    data: Array.from(
      { length: found.jumlah_rt },
      (_, i) => ({
        rw: rwClean,
        rt: String(i + 1).padStart(3, "0"),
      })
    ),
  };
};

/* =========================================================
   DATA RAW
========================================================= */

export const data = async (tahun, bulan) => {
  return request("data", {
    tahun,
    bulan: normalizeMonth(bulan),
  });
};

/* =========================================================
   REKAP
========================================================= */

export const rekap = async (tahun, bulan) => {
  return request("rekap", {
    tahun,
    bulan: normalizeMonth(bulan),
  });
};

/* =========================================================
   STATUS PELAPORAN
   Dibangun dari DATA AKTUAL, bukan default status.
========================================================= */

export const status = async (tahun, bulan) => {
  const dataResp = await data(tahun, bulan);

  const rawRows = Array.isArray(dataResp?.data)
    ? dataResp.data
    : [];

  const submitted = new Map();

  for (const row of rawRows) {
    const rw = normalizeRW(row?.rw);
    const rt = normalizeRT(row?.rt);

    if (!rw || !rt) continue;

    const key = `${rw}-${rt}`;

    const current = submitted.get(key);

    if (
      !current ||
      String(row?.timestamp || "") >
        String(current?.timestamp || "")
    ) {
      submitted.set(key, row);
    }
  }

  const result = [];

  for (const rwInfo of REGION_MASTER) {
    const rw = rwInfo.rw;

    for (let i = 1; i <= rwInfo.jumlah_rt; i++) {
      const rt = String(i).padStart(3, "0");
      const key = `${rw}-${rt}`;
      const row = submitted.get(key);

      if (row) {
        const ringkasan = row?.ringkasan || {};

        result.push({
          rw,
          rt,
          status: "SUDAH MENGISI",
          tanggal_submit: row?.timestamp || "",
          total_penduduk: ringkasan?.total_penduduk ?? null,
          laki_laki: ringkasan?.laki_laki ?? null,
          perempuan: ringkasan?.perempuan ?? null,
          id_laporan: row?.id_laporan ?? null,
        });
      } else {
        result.push({
          rw,
          rt,
          status: "BELUM MENGISI",
          tanggal_submit: null,
          total_penduduk: null,
          laki_laki: null,
          perempuan: null,
          id_laporan: null,
        });
      }
    }
  }

  const sudah = result.filter(
    (x) => x.status === "SUDAH MENGISI"
  ).length;

  const totalRt = 75;

  return {
    success: true,
    periode: `${String(bulan).charAt(0).toUpperCase()}${String(
      bulan
    ).slice(1)} ${tahun}`,
    total_rt: totalRt,
    sudah_mengisi: sudah,
    belum_mengisi: totalRt - sudah,
    persentase:
      totalRt > 0
        ? Number(((sudah / totalRt) * 100).toFixed(1))
        : 0,
    data: result,
  };
};

/* =========================================================
   REKAP PER RW
========================================================= */

export const rekapRw = async (tahun, bulan) => {
  const dataResp = await data(tahun, bulan);

  const rawRows = Array.isArray(dataResp?.data)
    ? dataResp.data
    : [];

  const latest = new Map();

  for (const row of rawRows) {
    const rw = normalizeRW(row?.rw);
    const rt = normalizeRT(row?.rt);

    if (!rw || !rt) continue;

    const key = `${rw}-${rt}`;
    const current = latest.get(key);

    if (
      !current ||
      String(row?.timestamp || "") >
        String(current?.timestamp || "")
    ) {
      latest.set(key, row);
    }
  }

  const output = [];

  for (const rwInfo of REGION_MASTER) {
    const rw = rwInfo.rw;
    const jumlahRt = rwInfo.jumlah_rt;

    let sudah = 0;
    let totalPenduduk = 0;
    let laki = 0;
    let perempuan = 0;
    let tetap = 0;
    let sementara = 0;

    for (let i = 1; i <= jumlahRt; i++) {
      const rt = String(i).padStart(3, "0");
      const row = latest.get(`${rw}-${rt}`);

      if (!row) continue;

      sudah++;

      const ringkasan = row?.ringkasan || {};

      totalPenduduk += Number(
        ringkasan?.total_penduduk || 0
      );

      laki += Number(ringkasan?.laki_laki || 0);
      perempuan += Number(ringkasan?.perempuan || 0);

      tetap += Number(
        ringkasan?.penduduk_tetap || 0
      );

      sementara += Number(
        ringkasan?.penduduk_sementara || 0
      );
    }

    output.push({
      rw,
      jumlah_rt: jumlahRt,
      sudah_mengisi: sudah,
      belum_mengisi: jumlahRt - sudah,
      progress:
        jumlahRt > 0
          ? Number(((sudah / jumlahRt) * 100).toFixed(1))
          : 0,
      total_penduduk: totalPenduduk,
      laki_laki: laki,
      perempuan,
      penduduk_tetap: tetap,
      penduduk_sementara: sementara,
    });
  }

  return {
    success: true,
    periode: `${String(bulan).charAt(0).toUpperCase()}${String(
      bulan
    ).slice(1)} ${tahun}`,
    data: output,
  };
};

/* =========================================================
   A.5 DETAIL
========================================================= */

const A5_GROUP_KEYS = [
  "tetap",
  "bandung",
  "jabar",
  "luar_jabar",
];

const A5_ROW_KEYS = [
  "bulan_lalu",
  "lahir",
  "meninggal",
  "pindah",
  "datang",
  "akhir",
];

const normalizeA5Cell = (cell) => {
  const source = cell && typeof cell === "object" ? cell : {};
  const l = Number(source.l) || 0;
  const p = Number(source.p) || 0;

  return {
    l,
    p,
    jumlah: source.jumlah != null ? Number(source.jumlah) || 0 : l + p,
  };
};

const normalizeA5Data = (value) => {
  if (!value || typeof value !== "object") return value;

  const kelompok = {};

  for (const group of A5_GROUP_KEYS) {
    const sourceGroup = value.kelompok?.[group] || {};
    kelompok[group] = {};

    for (const rowKey of A5_ROW_KEYS) {
      kelompok[group][rowKey] = normalizeA5Cell(sourceGroup[rowKey]);
    }
  }

  return {
    ...value,
    kelompok,
  };
};

const normalizeA5Response = (response) => {
  if (!response || typeof response !== "object") return response;

  return {
    ...response,
    data: normalizeA5Data(response.data),
  };
};

export const a5 = async (tahun, bulan, rw, rt) => {
  const rwClean = normalizeRW(rw);
  const rtClean = normalizeRT(rt);

  if (!rwClean || !rtClean) {
    return {
      success: false,
      data: null,
      message: "RW dan RT harus dipilih.",
    };
  }

  try {
    const direct = await request("a5", {
      tahun,
      bulan: normalizeMonth(bulan),
      rw: rwClean,
      rt: rtClean,
    });

    if (direct?.success && direct?.data) {
      return normalizeA5Response(direct);
    }
  } catch (error) {
    console.warn(
      "Direct A.5 gagal, mencoba fallback data:",
      error
    );
  }

  /* FALLBACK: ambil data aktual */
  const dataResp = await data(tahun, bulan);
  const rows = Array.isArray(dataResp?.data)
    ? dataResp.data
    : [];

  let found = null;

  for (const row of rows) {
    const rowRw = normalizeRW(row?.rw);
    const rowRt = normalizeRT(row?.rt);

    if (
      rowRw === rwClean &&
      rowRt === rtClean
    ) {
      if (
        !found ||
        String(row?.timestamp || "") >
          String(found?.timestamp || "")
      ) {
        found = row;
      }
    }
  }

  if (!found) {
    return {
      success: true,
      data: null,
      message:
        "Belum ada laporan untuk periode yang dipilih.",
    };
  }

  return {
    success: true,
    data: normalizeA5Data(found),
  };
};

/* =========================================================
   A.5 AGREGAT
========================================================= */

const ROW_KEYS = [
  "bulan_lalu",
  "lahir",
  "meninggal",
  "pindah",
  "datang",
  "akhir",
];

const GROUP_KEYS = [
  "tetap",
  "bandung",
  "jabar",
  "luar_jabar",
];

const emptyKelompok = () => {
  const kelompok = {};

  for (const group of GROUP_KEYS) {
    kelompok[group] = {};

    for (const rowKey of ROW_KEYS) {
      kelompok[group][rowKey] = {
        l: 0,
        p: 0,
      };
    }

    kelompok[group].akhir = {
      l: 0,
      p: 0,
      jumlah: 0,
    };
  }

  return kelompok;
};

const accumulateKelompok = (agg, row) => {
  const kelompok = row?.kelompok || {};

  for (const group of GROUP_KEYS) {
    const sourceGroup = kelompok?.[group] || {};

    for (const rowKey of ROW_KEYS) {
      const source = sourceGroup?.[rowKey] || {};

      agg[group][rowKey].l += Number(
        source?.l || 0
      );

      agg[group][rowKey].p += Number(
        source?.p || 0
      );
    }
  }

  return agg;
};

export const a5Agregat = async (
  tahun,
  bulan,
  scope = "kelurahan",
  rw = null
) => {
  const dataResp = await data(tahun, bulan);

  const rows = Array.isArray(dataResp?.data)
    ? dataResp.data
    : [];

  const latest = new Map();

  for (const row of rows) {
    const rwValue = normalizeRW(row?.rw);
    const rtValue = normalizeRT(row?.rt);

    if (!rwValue || !rtValue) continue;

    if (
      scope === "rw" &&
      rw &&
      rwValue !== normalizeRW(rw)
    ) {
      continue;
    }

    const key = `${rwValue}-${rtValue}`;

    const current = latest.get(key);

    if (
      !current ||
      String(row?.timestamp || "") >
        String(current?.timestamp || "")
    ) {
      latest.set(key, row);
    }
  }

  const kelompok = emptyKelompok();

  let jumlahLaporan = 0;

  for (const row of latest.values()) {
    accumulateKelompok(kelompok, row);
    jumlahLaporan++;
  }

  for (const group of GROUP_KEYS) {
    kelompok[group].akhir.jumlah =
      kelompok[group].akhir.l +
      kelompok[group].akhir.p;
  }

  const totalL = GROUP_KEYS.reduce(
    (sum, group) =>
      sum + kelompok[group].akhir.l,
    0
  );

  const totalP = GROUP_KEYS.reduce(
    (sum, group) =>
      sum + kelompok[group].akhir.p,
    0
  );

  return {
    success: true,
    data: {
      scope,
      tahun,
      bulan: normalizeMonth(bulan),
      periode: `${String(bulan).charAt(0).toUpperCase()}${String(
        bulan
      ).slice(1)} ${tahun}`,
      kelurahan: "Suka Asih",
      rw: rw ? normalizeRW(rw) : "SELURUH RW",
      rt: scope === "rw" ? "SELURUH RT" : "-",
      kelompok,
      ringkasan: {
        laki_laki: totalL,
        perempuan: totalP,
        total_penduduk: totalL + totalP,
        jumlah_laporan: jumlahLaporan,
      },
    },
  };
};

/* =========================================================
   TREN TAHUNAN
========================================================= */

export const tren = async (tahun, bulanAktif, rekapAktif) => {
  const months = MONTHS.map((m) => m.value);

  const results = await Promise.all(
    months.map(async (bulan) => {
      const response =
        bulan === normalizeMonth(bulanAktif) && rekapAktif
          ? rekapAktif
          : await rekap(tahun, bulan);
      const summary = response?.summary || {};

      return {
        bulan,
        total_penduduk:
          Number(summary?.total_penduduk || 0),
        laki_laki:
          Number(summary?.laki_laki || 0),
        perempuan:
          Number(summary?.perempuan || 0),
        kelahiran:
          Number(summary?.kelahiran || 0),
        kematian:
          Number(summary?.kematian || 0),
        pindah:
          Number(summary?.pindah || 0),
        datang:
          Number(summary?.datang || 0),
        jumlah_laporan:
          Number(summary?.jumlah_laporan || 0),
      };
    })
  );

  return {
    success: true,
    tahun,
    data: results,
  };
};

/* =========================================================
   MASTER ASET
   Phase 1: localStorage agar frontend tidak bergantung
   pada MongoDB Emergent.
========================================================= */

const ASSET_STORAGE_KEY =
  "silapdi_master_aset_v1";

const readAssets = () => {
  try {
    const raw =
      window.localStorage.getItem(
        ASSET_STORAGE_KEY
      );

    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveAssets = (items) => {
  window.localStorage.setItem(
    ASSET_STORAGE_KEY,
    JSON.stringify(items)
  );
};

export const masterAsetList = async () => ({
  success: true,
  data: readAssets(),
});

export const masterAsetPut = async (body) => {
  const items = readAssets();

  const scope = body?.scope || "";
  const rw = body?.rw
    ? normalizeRW(body.rw)
    : null;

  const rt = body?.rt
    ? normalizeRT(body.rt)
    : null;

  const key = JSON.stringify({
    scope,
    rw,
    rt,
  });

  const payload = {
    ...body,
    scope,
    rw,
    rt,
  };

  const index = items.findIndex(
    (item) =>
      JSON.stringify({
        scope: item?.scope || "",
        rw: item?.rw
          ? normalizeRW(item.rw)
          : null,
        rt: item?.rt
          ? normalizeRT(item.rt)
          : null,
      }) === key
  );

  if (index >= 0) {
    items[index] = { ...items[index], ...payload };
  } else {
    items.push(payload);
  }

  saveAssets(items);

  return {
    success: true,
    data: payload,
  };
};

export const masterAsetDelete = async (params = {}) => {
  const items = readAssets();

  const scope = params?.scope || "";
  const rw = params?.rw
    ? normalizeRW(params.rw)
    : null;
  const rt = params?.rt
    ? normalizeRT(params.rt)
    : null;

  const filtered = items.filter(
    (item) =>
      !(
        (item?.scope || "") === scope &&
        (item?.rw
          ? normalizeRW(item.rw)
          : null) === rw &&
        (item?.rt
          ? normalizeRT(item.rt)
          : null) === rt
      )
  );

  saveAssets(filtered);

  return {
    success: true,
  };
};

/* =========================================================
   MONTHS / YEARS / FORMATTERS
========================================================= */

export const MONTHS = [
  { value: "januari", label: "Januari" },
  { value: "februari", label: "Februari" },
  { value: "maret", label: "Maret" },
  { value: "april", label: "April" },
  { value: "mei", label: "Mei" },
  { value: "juni", label: "Juni" },
  { value: "juli", label: "Juli" },
  { value: "agustus", label: "Agustus" },
  { value: "september", label: "September" },
  { value: "oktober", label: "Oktober" },
  { value: "november", label: "November" },
  { value: "desember", label: "Desember" },
];

export const YEARS = [
  "2024",
  "2025",
  "2026",
  "2027",
];

export const monthLabel = (m) =>
  MONTHS.find((x) => x.value === m)?.label || m;

export const fmtNum = (n) => {
  if (
    n === null ||
    n === undefined ||
    n === ""
  ) {
    return "-";
  }

  const value =
    typeof n === "number"
      ? n
      : Number(n);

  if (Number.isNaN(value)) return "-";

  return value.toLocaleString("id-ID");
};

export const fmtDate = (s) => {
  if (!s) return "-";
  return s;
};

/* =========================================================
   BACKWARD COMPATIBILITY
========================================================= */

export const API = "/api";

export const api = {
  regions,
  rekap,
  status,
  data,
  rekapRw,
  a5,
  a5Agregat,
  tren,
  listRt,
  masterAsetList,
  masterAsetPut,
  masterAsetDelete,
};

export default api;